/* eslint-disable @typescript-eslint/ban-types */
const billboardSizesDefault = [
  [970, 90],
  [728, 90],
  [970, 250],
] as const;

const rectangleSizesDefault = [
  [300, 250],
  [336, 280],
] as const;

const rectangleSizesDefaultPlus = [
  [160, 600],
  [300, 50],
  [300, 75],
  [300, 100],
  [300, 600],
  [320, 50],
  [320, 100],
  [320, 480],
  ...rectangleSizesDefault,
] as const;

type AdsDefsType = {
  [key: string]: {
    sizes?: number[][];
    enableMobile?: boolean;
    enableDesktop?: boolean;
    children?: AdsDefsType;
  };
};

const AdsDefs = {
  billboard: {
    sizes: billboardSizesDefault,
    enableMobile: false,
    enableDesktop: true,
    children: {
      main: {
        children: {
          home: {
            sizes: billboardSizesDefault,
            enableMobile: false,
            enableDesktop: true,
          },
          listing: {
            sizes: billboardSizesDefault,
            enableMobile: false,
            enableDesktop: true,
          },
        },
      },
      footer: {
        sizes: billboardSizesDefault,
        enableMobile: false,
        enableDesktop: true,
      },
    },
  },
  rectangle: {
    children: {
      main: {
        children: {
          home: {
            sizes: rectangleSizesDefaultPlus,
            enableMobile: true,
            enableDesktop: false,
          },
          listing: {
            sizes: rectangleSizesDefaultPlus,
            enableMobile: true,
            enableDesktop: false,
          },
          internal: {
            sizes: rectangleSizesDefaultPlus,
            enableMobile: true,
            enableDesktop: true,
          },
        },
      },
      side: {
        sizes: rectangleSizesDefault,
        enableMobile: false,
        enableDesktop: true,
      },
      footer: {
        sizes: rectangleSizesDefaultPlus,
        enableMobile: true,
        enableDesktop: false,
      },
      collector: {
        sizes: rectangleSizesDefault,
        enableMobile: true,
        enableDesktop: true,
      },
    },
  },
  halfpage: {
    sizes: [[300, 600], [120, 600], [160, 600], ...rectangleSizesDefault],
    enableMobile: false,
    enableDesktop: true,
  },
  background: {
    children: {
      'bg-topo-desktop': {
        sizes: [[2000, 750], ...billboardSizesDefault],
        enableMobile: false,
        enableDesktop: true,
      },
      'topo-mobile': {
        sizes: [
          [2000, 750],
          [300, 50],
          [300, 100],
          [320, 50],
          [320, 100],
        ],
        enableMobile: true,
        enableDesktop: false,
      },
    },
  },
  anchor: {
    sizes: [
      [320, 50],
      [300, 50],
      [1, 1]
    ],
    enableMobile: true,
    enableDesktop: false,
  },
  Anchor_Footer: {
    sizes: [
      [728, 90],
      [970, 90],
      [1, 1]
    ],
    enableMobile: false,
    enableDesktop: true,
  },
  Interstitial_2023: {
    sizes: [
      [336, 280],
      [300, 250],
      [320, 480],
    ],
    enableMobile: true,
    enableDesktop: true,
  },
  '1x1': {
    sizes: [[1, 1]],
    enableMobile: true,
    enableDesktop: true,
  },
} as const;

export function getSizesByAdType(adType: string) {
  const adTypeSplit = adType.replace(/-\d+$/, '').split('/');
  let sizes = [[] as number[]];

  let currentObj = AdsDefs;
  adTypeSplit.forEach((key, index) => {
    if (currentObj[key]?.children && index < adTypeSplit.length - 1) {
      currentObj = currentObj[key]?.children;
    } else {
      sizes = currentObj[key]?.sizes;
    }
  });

  return sizes;
}

export function getDevicesByAdType(adType: string) {
  const adTypeSplit = adType.replace(/-\d+$/, '').split('/');

  let devices: { desktop: boolean; mobile: boolean };

  let currentObj = AdsDefs;
  adTypeSplit.forEach((key, index) => {
    if (currentObj[key]?.children && index < adTypeSplit.length - 1) {
      currentObj = currentObj[key]?.children;
    } else {
      devices = { desktop: currentObj[key]?.enableDesktop, mobile: currentObj[key]?.enableMobile };
    }
  });

  return devices;
}

type EnableMobileKeys<T> = {
  [K in keyof T]: T[K] extends { enableMobile: true }
    ?
        | K
        | (T[K] extends object
            ? K extends string
              ? K extends 'children'
                ? EnableMobileKeys<T[K]> extends infer U
                  ? U extends string | number | bigint | boolean
                    ? U
                    : never
                  : never
                : K extends 'sizes'
                  ? never
                  : `${K}/${EnableMobileKeys<T[K]> extends infer U ? (U extends string | number | bigint | boolean ? U : never) : never}`
              : never
            : never)
    : T[K] extends object
      ? K extends string
        ? K extends 'children'
          ? EnableMobileKeys<T[K]> extends infer U
            ? U extends string | number | bigint | boolean
              ? U
              : never
            : never
          : K extends 'sizes'
            ? never
            : `${K}/${EnableMobileKeys<T[K]> extends infer U ? (U extends string | number | bigint | boolean ? U : never) : never}`
        : never
      : never;
}[keyof T];

type EnableDesktopKeys<T> = {
  [K in keyof T]: T[K] extends { enableDesktop: true }
    ?
        | K
        | (T[K] extends object
            ? K extends string
              ? K extends 'children'
                ? EnableDesktopKeys<T[K]> extends infer U
                  ? U extends string | number | bigint | boolean
                    ? U
                    : never
                  : never
                : K extends 'sizes'
                  ? never
                  : `${K}/${EnableDesktopKeys<T[K]> extends infer U ? (U extends string | number | bigint | boolean ? U : never) : never}`
              : never
            : never)
    : T[K] extends object
      ? K extends string
        ? K extends 'children'
          ? EnableDesktopKeys<T[K]> extends infer U
            ? U extends string | number | bigint | boolean
              ? U
              : never
            : never
          : K extends 'sizes'
            ? never
            : `${K}/${EnableDesktopKeys<T[K]> extends infer U ? (U extends string | number | bigint | boolean ? U : never) : never}`
        : never
      : never;
}[keyof T];

export type MobileAdOptions = EnableMobileKeys<typeof AdsDefs>;
export type DesktopAdOptions = EnableDesktopKeys<typeof AdsDefs>;

export type AllAdOptions = MobileAdOptions | DesktopAdOptions;
